import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowLeft,
  Calendar,
  User,
  Tag,
  Share2,
  BookOpen,
} from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API}/blog/${slug}`);
      if (!response.ok) {
        throw new Error("Post not found");
      }
      const data = await response.json();
      setPost(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rnb-blue"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-rnb-gray/30 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
            Article Not Found
          </h2>
          <p className="text-rnb-gray mb-6">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={post.title ? `${post.title} - RNB Consulting Blog` : "Healthcare Practice Blog Post"}
        description={post.excerpt || post.content?.substring(0, 160) || "Read insights on healthcare operations, staff management, and practice growth from RNB Consulting Group."}
      />
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-rnb-light to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-rnb-blue hover:text-rnb-blue/80 mb-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>

          {post.category && (
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-rnb-blue" />
              <span className="text-sm font-medium text-rnb-blue uppercase tracking-wide">
                {post.category}
              </span>
            </div>
          )}

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-rnb-navy mb-6">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-rnb-gray mb-8">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-rnb-blue transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {post.tags.map((tag, index) => (
                <span
                  key={tag.title || tag.name || `tag-${index}`}
                  className="px-3 py-1 bg-slate-100 text-rnb-gray text-xs rounded-full"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="aspect-video rounded-xl overflow-hidden shadow-lg -mt-8 relative z-10">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article 
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-rnb-navy prose-p:text-rnb-gray prose-a:text-rnb-blue prose-strong:text-rnb-navy"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />
        </div>
      </section>

      {/* Author Card */}
      <section className="py-8 bg-white border-t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 bg-slate-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-rnb-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-rnb-blue" />
                </div>
                <div>
                  <p className="text-sm text-rnb-gray mb-1">Written by</p>
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                    {post.author_name}
                  </h3>
                  <p className="text-sm text-rnb-gray">
                    RNB Consulting Group provides expert healthcare business consulting 
                    to help practices thrive.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-rnb-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Need Help with Your Practice?
          </h2>
          <p className="text-slate-300 mb-8">
            Our team is ready to provide personalized guidance for your healthcare business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation">
              <Button size="lg" className="btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90">
                Schedule Free Consultation
              </Button>
            </Link>
            <Link to="/blog">
              <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10">
                Browse More Articles
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
